import React, { useRef, useState, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { FaCopy, FaFilePdf } from "react-icons/fa";

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------
const BRAND      = "#FEC025";
const BRAND_DARK = "#C9960E";
const DOC_BG     = "#ffffff";
const HEADER_FG  = "#1a1a1a";
const BODY_FG    = "#2d2d2d";
const BORDER     = "#d0d0d0";
const ROW_ALT    = "#fafafa";
const SECTION_CAP = "#3a3a3a";
const FONT = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";

// ---------------------------------------------------------------------------
// Row definitions
// ---------------------------------------------------------------------------
const SCHEDULE_MILESTONE_ROWS = [
  { label: "Site Prep",               key: "sitePrep" },
  { label: "Demolition and Survey",   key: "demolitionAndSurvey" },
  { label: "Excavation",              key: "excavation" },
  { label: "Footings and Foundation", key: "footingAndFoundation" },
  { label: "Framing",                 key: "framing" },
  { label: "Roofing and Mechanical",  key: "roofingAndMechanical" },
  { label: "Exterior Finish",         key: "exteriorFinish" },
  { label: "Insulation and Drywall",  key: "insulationAndDrywall" },
  { label: "Construction Completion", key: "Completion" },
  { label: "Project Turn Over",       key: "turnOver" },
  { label: "Final Invoice",           key: "finalInvoice" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatCurrency = (val) => {
  const n = Number(val);
  if (isNaN(n) || val === "" || val == null) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Bold gold section-header bar */
const SectionHeader = ({ children }) => (
  <div style={{
    background: BRAND,
    borderLeft: `5px solid ${BRAND_DARK}`,
    padding: "9px 14px",
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: "12.5px",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: HEADER_FG,
  }}>
    {children}
  </div>
);

/** Date badge with label above */
const DateBadge = ({ label, value, bg }) => (
  <div style={{ textAlign: "center", minWidth: "170px" }}>
    <div style={{ fontSize: "11px", color: "#666", marginBottom: "5px", fontFamily: FONT }}>{label}</div>
    <div style={{
      background: bg,
      border: `1px solid ${BRAND_DARK}`,
      borderRadius: "4px",
      padding: "5px 20px",
      fontFamily: FONT,
      fontWeight: 600,
      fontSize: "13px",
      color: HEADER_FG,
    }}>
      {value || "—"}
    </div>
  </div>
);

/** Table wrapper */
const DocTable = ({ children, style }) => (
  <table style={{ borderCollapse: "collapse", border: "none", width: "100%", fontFamily: FONT, fontSize: "12.5px", ...style }}>
    {children}
  </table>
);

/** TH cell */
const TH = ({ children, style }) => (
  <th style={{
    background: "#f0f0f0",
    border: "none",
    // borderBottom: `1px solid ${BORDER}`,
    // borderRight: `1px solid ${BORDER}`,
    padding: "7px 10px",
    fontWeight: 600,
    fontSize: "11.5px",
    color: SECTION_CAP,
    textAlign: "center",
    letterSpacing: "0.3px",
    ...style,
  }}>
    {children}
  </th>
);

/** TD cell */
const TD = ({ children, center, bold, alt, style }) => (
  <td style={{
    border: "none",
    // borderBottom: `1px solid ${BORDER}`,
    // borderRight: `1px solid ${BORDER}`,
    padding: "7px 10px",
    verticalAlign: "top",
    backgroundColor: alt ? ROW_ALT : DOC_BG,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    textAlign: center ? "center" : "left",
    fontWeight: bold ? 700 : 400,
    color: BODY_FG,
    fontFamily: FONT,
    fontSize: "12.5px",
    ...style,
  }}>
    {children}
  </td>
);

// ---------------------------------------------------------------------------
// WeeklyUpdatePreview
// Props:
//   visible         â€“ boolean
//   onHide          â€“ () => void
//   values          â€“ formik.values snapshot
//   internalJob     â€“ "Y" | "N"
// ---------------------------------------------------------------------------
const WeeklyUpdatePreview = ({ visible, onHide, values, internalJob }) => {
  const previewRef = useRef(null);
  const toast      = useRef(null);
  const [isCopyLoading, setIsCopyLoading] = useState(false);
  const [isPdfLoading,  setIsPdfLoading]  = useState(false);

  // Derived budget totals
  const jobRunningTotal =
    Number(values?.contractPriceAmount     || 0) +
    Number(values?.approvedChangeOrderPrice || 0) +
    Number(values?.allowanceDiffrence      || 0);
  const balance =
    jobRunningTotal +
    Number(values?.gst       || 0) -
    Number(values?.amountPaid || 0);

  // â”€â”€ capture â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const capturePreview = useCallback(async (pixelRatio = 2) => {
    if (!previewRef.current) return null;
    return await toPng(previewRef.current, { cacheBust: true, pixelRatio, backgroundColor: "#ffffff" });
  }, []);

  const handleCopy = useCallback(async () => {
    setIsCopyLoading(true);
    try {
      const imgSrc = await capturePreview(2);
      if (!imgSrc) { setIsCopyLoading(false); return; }
      const img = new Image();
      img.src = imgSrc;
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
      const canvas = document.createElement("canvas");
      canvas.width = img.width; canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) { setIsCopyLoading(false); return; }
        if (navigator.clipboard?.write) {
          navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })])
            .then(() => toast.current?.show({ severity: "success", summary: "Copied", detail: "Image copied to clipboard" }))
            .catch(() => toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to copy to clipboard" }))
            .finally(() => setIsCopyLoading(false));
        } else {
          toast.current?.show({ severity: "warn", summary: "Unavailable", detail: "Clipboard API not supported" });
          setIsCopyLoading(false);
        }
      }, "image/png");
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to capture preview" });
      setIsCopyLoading(false);
    }
  }, [capturePreview]);

  const handleDownloadPdf = useCallback(async () => {
    setIsPdfLoading(true);
    try {
      const imgSrc = await capturePreview(1.5);
      if (!imgSrc) { setIsPdfLoading(false); return; }
      const img = new Image();
      img.src = imgSrc;
      await new Promise((res) => { img.onload = res; });

      const pdf       = new jsPDF("p", "mm", "a4");
      const pdfW      = pdf.internal.pageSize.getWidth();
      const pdfH      = pdf.internal.pageSize.getHeight();
      const pageCanvH = (pdfH * img.width) / pdfW;

      const full = document.createElement("canvas");
      full.width = img.width; full.height = img.height;
      const fc = full.getContext("2d");
      fc.fillStyle = "#fff"; fc.fillRect(0, 0, full.width, full.height);
      fc.drawImage(img, 0, 0);

      let left = img.height, curY = 0, page = 0;
      while (left > 0) {
        if (page++ > 0) pdf.addPage();
        const sliceH = Math.min(left, pageCanvH);
        const pc = document.createElement("canvas");
        pc.width = img.width; pc.height = sliceH;
        const ctx = pc.getContext("2d");
        ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, pc.width, pc.height);
        ctx.drawImage(full, 0, curY, img.width, sliceH, 0, 0, img.width, sliceH);
        pdf.addImage(pc.toDataURL("image/jpeg", 0.82), "JPEG", 0, 0, pdfW, (sliceH * pdfW) / img.width);
        left -= sliceH; curY += sliceH;
      }

      const now = new Date();
      const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit",
        day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
      });
      const p = {}; fmt.formatToParts(now).forEach((x) => { p[x.type] = x.value; });
      const ts = `${p.year}-${p.month}-${p.day}_${p.hour}-${p.minute}`;
      const projectLabel = values?.ProjectName ? ` - ${values.ProjectName}` : "";
      pdf.save(`Weekly Update${projectLabel} - ${ts}.pdf`);
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to generate PDF" });
    } finally {
      setIsPdfLoading(false);
    }
  }, [capturePreview, values]);

  // â”€â”€ footer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const dialogFooter = (
    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
      <Button icon={<FaCopy />}    tooltip="Copy Weekly Update" tooltipOptions={{ position: "top" }} loading={isCopyLoading} onClick={handleCopy}       className="dm-btn-copy" type="button" />
      <Button icon={<FaFilePdf />} tooltip="Download PDF"       tooltipOptions={{ position: "top" }} loading={isPdfLoading}  onClick={handleDownloadPdf} className="dm-btn-pdf"  type="button" />
    </div>
  );

  if (!values) return null;

  const projectName = values.ProjectName || "";
  const weekFrom    = formatDate(values.hereIsYourWeeklySummaryFrom);
  const weekTo      = formatDate(values.hereIsYourWeeklySummaryTo);
  const weekRange   = (weekFrom !== "—" || weekTo !== "—") ? `${weekFrom} — ${weekTo}` : "";

  // â”€â”€ render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  return (
    <Dialog
      header={`Weekly Update Preview${projectName ? ` — ${projectName}` : ""}`}
      visible={visible}
      onHide={onHide}
      style={{ width: "92vw", maxWidth: "1180px" }}
      maximizable
      footer={dialogFooter}
      contentStyle={{ overflowY: "auto", padding: "20px", background: "#f2f2f2" }}
    >
      <Toast ref={toast} />

      {/* â”€â”€â”€ Printable document â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div style={{ maxWidth: "900px", margin: "0 auto", boxShadow: "0 2px 12px rgba(0,0,0,0.10)", borderRadius: "6px", overflow: "hidden" }}>
      <div ref={previewRef} style={{
        background: DOC_BG,
        fontFamily: FONT,
        fontSize: "13px",
        color: BODY_FG,
        width: "900px",
        boxSizing: "border-box",
      }}>

        {/* â•â• Document title block â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        <div style={{
          background: BRAND,
          padding: "28px 32px 22px",
          borderBottom: `4px solid ${BRAND_DARK}`,
        }}>
          <div style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px", textTransform: "uppercase", color: "#7a5a00", marginBottom: "6px", fontFamily: FONT }}>
            Construction Manager Playbook
          </div>
          <div style={{ fontSize: "24px", fontWeight: 800, color: "#1a1a1a", lineHeight: 1.2, marginBottom: "8px", fontFamily: FONT }}>
            Weekly Project Manager Update
          </div>
          {projectName && (
            <div style={{ fontSize: "16px", fontWeight: 600, color: "#3a2a00", marginBottom: "6px", fontFamily: FONT }}>
              {projectName}
            </div>
          )}
          {weekRange && (
            <div style={{ fontSize: "12px", color: "#5a4000", fontFamily: FONT }}>
              Period:&nbsp;<strong>{weekRange}</strong>
            </div>
          )}
        </div>

        {/* â•â• Body â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "22px" }}>

          {/* â”€â”€ Overview â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <div>
            <SectionHeader>Project Overview</SectionHeader>
            <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", background: DOC_BG, padding: "16px 18px" }}>

              <div style={{ marginBottom: "14px" }}>
                <span style={{ fontWeight: 600, fontSize: "12px", color: SECTION_CAP, fontFamily: FONT }}>Dear:&nbsp;</span>
                <span style={{ fontSize: "13px", fontFamily: FONT }}>{values.dear || "â€”"}</span>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontWeight: 700, fontSize: "11px", color: SECTION_CAP, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: FONT }}>
                  Project Summary
                </div>
                <div style={{ lineHeight: 1.7, fontSize: "13px", whiteSpace: "pre-wrap", color: BODY_FG, padding: "10px 14px", background: ROW_ALT, borderRadius: "4px", border: `1px solid ${BORDER}`, fontFamily: FONT }}>
                   {values.projectSummary || "—"}
                </div>
              </div>

              <div style={{ fontWeight: 700, fontSize: "11px", color: SECTION_CAP, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: FONT }}>
                Schedule
              </div>
              <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                <DateBadge label="Construction Start Date" value={formatDate(values.scheduleConstructionStartDate)} bg="#FFF9E0" />
                <DateBadge
                  label={internalJob === "Y" ? "Construction Completion" : "Project Turn Over"}
                  value={formatDate(values.scheduleProjectCompletion)}
                  bg={internalJob === "Y" ? "#d4edda" : "#FFE8D6"}
                />
              </div>
            </div>
          </div>

          {/* â”€â”€ Schedule Milestones â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          {(values.scheduleMilestonesSectionList || []).map((milestones, idx) => (
            <div key={idx}>
              <SectionHeader>Schedule Milestones{idx > 0 ? ` — Group ${idx + 1}` : ""}</SectionHeader>
              <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
              <DocTable>
                <thead>
                  <tr>
                    <TH style={{ textAlign: "left", width: "26%" }}>Milestone</TH>
                    <TH style={{ width: "15%" }}>Planned Baseline</TH>
                    <TH style={{ width: "15%" }}>Current Forecast</TH>
                    <TH style={{ width: "14%" }}>Actual Completion</TH>
                    <TH>Notes</TH>
                  </tr>
                </thead>
                <tbody>
                  {SCHEDULE_MILESTONE_ROWS.map(({ label, key }, i) => (
                    <tr key={key}>
                      <TD alt={i % 2 === 1} style={{ fontWeight: 500 }}>{label}</TD>
                      <TD alt={i % 2 === 1} center>{formatDate(milestones[`${key}_P_B_C_Date`])}</TD>
                      <TD alt={i % 2 === 1} center>{formatDate(milestones[`${key}_C_F_C_Date`])}</TD>
                      <TD alt={i % 2 === 1} center>{formatDate(milestones[`${key}_A_C_Date`])}</TD>
                      <TD alt={i % 2 === 1}>{milestones[`${key}_Notes`] || ""}</TD>
                    </tr>
                  ))}
                </tbody>
              </DocTable>
              <div style={{
                background: BRAND, borderTop: `1px solid ${BRAND_DARK}`,
                textAlign: "center", fontStyle: "italic", fontWeight: 700,
                fontSize: "13px", padding: "10px", fontFamily: FONT, color: "#3a2a00",
              }}>
                "A day late is a day stolen from a client's life"
              </div>
              </div>
            </div>
          ))}

          {/* â”€â”€ Activities Worked On This Week â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <div>
            <SectionHeader>Activities Worked On This Week</SectionHeader>
            <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
            <DocTable>
              <thead>
                <tr>
                  <TH style={{ textAlign: "left", width: "34%" }}>Description</TH>
                  <TH style={{ width: "16%" }}>Contractor</TH>
                  <TH>Brief Scope of Work</TH>
                  <TH style={{ width: "155px" }}>Forecasted Completion</TH>
                </tr>
              </thead>
              <tbody>
                {(values.section_9 || []).map((item, i) => (
                  <tr key={i}>
                    <TD alt={i % 2 === 1}>{item.activitiesworkedOnThisWeek}</TD>
                    <TD alt={i % 2 === 1}>{item.Constractor}</TD>
                    <TD alt={i % 2 === 1}>{item.briefScopeOfWeek}</TD>
                    <TD alt={i % 2 === 1} center>{formatDate(item.CurrentForecastedCompletionDate)}</TD>
                  </tr>
                ))}
              </tbody>
            </DocTable>
            </div>
          </div>

          {/* â”€â”€ Activities Scheduled For Next Week â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <div>
            <SectionHeader>Activities Scheduled For Next Week</SectionHeader>
            <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
            <DocTable>
              <thead>
                <tr>
                  <TH style={{ textAlign: "left", width: "34%" }}>Description</TH>
                  <TH style={{ width: "16%" }}>Contractor</TH>
                  <TH>Brief Scope of Work</TH>
                  <TH style={{ width: "155px" }}>Forecasted Completion</TH>
                </tr>
              </thead>
              <tbody>
                {(values.section_10 || []).map((item, i) => (
                  <tr key={i}>
                    <TD alt={i % 2 === 1}>{item.activitiesScheduledOnNextWeek}</TD>
                    <TD alt={i % 2 === 1}>{item.Constractor}</TD>
                    <TD alt={i % 2 === 1}>{item.briefScopeOfWeek}</TD>
                    <TD alt={i % 2 === 1} center>{formatDate(item.CurrentForecastedCompletionDate)}</TD>
                  </tr>
                ))}
              </tbody>
            </DocTable>
            </div>
          </div>

          {/* â”€â”€ Budget + Deposits (side by side) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              <SectionHeader>Project Budget</SectionHeader>
              <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
              <DocTable>
                <thead><tr>
                  <TH style={{ textAlign: "left" }}>Item</TH>
                  <TH style={{ width: "130px" }}>Amount</TH>
                </tr></thead>
                <tbody>
                  {[
                    { label: "Contract Price",                            val: values.contractPriceAmount },
                    { label: "Approved Change Order Price",               val: values.approvedChangeOrderPrice },
                    { label: "Allowance Difference (Selections to Date)", val: values.allowanceDiffrence },
                    { label: "Job Running Total",                         val: jobRunningTotal, bold: true },
                    { label: "GST",                                       val: values.gst },
                    { label: "Amount Paid",                               val: values.amountPaid },
                    { label: "Balance (Not Including Deposit)",           val: balance, bold: true },
                  ].map(({ label, val, bold }, i) => (
                    <tr key={label}>
                      <TD alt={i % 2 === 1} style={{ fontWeight: bold ? 700 : 400 }}>{label}</TD>
                      <TD alt={i % 2 === 1} center bold={bold}>{formatCurrency(val)}</TD>
                    </tr>
                  ))}
                </tbody>
              </DocTable>
              </div>
            </div>
            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              <SectionHeader>Project Deposits</SectionHeader>
              <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
              <DocTable>
                <thead><tr>
                  <TH style={{ textAlign: "left" }}>Item</TH>
                  <TH style={{ width: "130px" }}>Amount</TH>
                </tr></thead>
                <tbody>
                  {[
                    { label: "Contract Deposits",       val: values.contractDepositsAmount },
                    { label: "Building Permit Deposit", val: values.buildingPermitDepositAmount },
                    { label: "Other Deposits",          val: values.otherDepositAmount },
                    { label: "Total Deposits",          val: values.totalDepositAmount, bold: true },
                  ].map(({ label, val, bold }, i) => (
                    <tr key={label}>
                      <TD alt={i % 2 === 1} style={{ fontWeight: bold ? 700 : 400 }}>{label}</TD>
                      <TD alt={i % 2 === 1} center bold={bold}>{formatCurrency(val)}</TD>
                    </tr>
                  ))}
                </tbody>
              </DocTable>
              </div>
            </div>
          </div>

          {/* â”€â”€ Change Work Orders â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <div>
            <SectionHeader>Change Work Orders</SectionHeader>
            <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
            <DocTable>
              <thead>
                <tr>
                  <TH style={{ textAlign: "left", width: "30%" }}>Description</TH>
                  <TH style={{ width: "115px" }}>Date</TH>
                  <TH style={{ width: "120px" }}>Amount</TH>
                  <TH style={{ width: "90px" }}>Approved</TH>
                  <TH>Notes</TH>
                </tr>
              </thead>
              <tbody>
                {(values.section_13 || []).map((item, i) => (
                  <tr key={i}>
                    <TD alt={i % 2 === 1}>{item.changeWorkOrder}</TD>
                    <TD alt={i % 2 === 1} center>{formatDate(item.date)}</TD>
                    <TD alt={i % 2 === 1} center>{formatCurrency(item.amount)}</TD>
                    <TD alt={i % 2 === 1} center>{item.approved}</TD>
                    <TD alt={i % 2 === 1}>{item.notes}</TD>
                  </tr>
                ))}
              </tbody>
            </DocTable>
            </div>
          </div>

          {/* â”€â”€ Action Items Discussed â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <div>
            <SectionHeader>Action Items Discussed</SectionHeader>
            <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
            <DocTable>
              <tbody>
                {(values.section_14 || []).map((item, i) => (
                  <tr key={i}>
                    <TD alt={i % 2 === 1} style={{ paddingLeft: "14px" }}>
                      <span style={{ color: BRAND_DARK, fontWeight: 700, marginRight: "8px", fontFamily: FONT }}>{i + 1}.</span>
                      {item.actionItemDiscussed}
                    </TD>
                  </tr>
                ))}
              </tbody>
            </DocTable>
            </div>
          </div>

          {/* â”€â”€ Document footer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
          <div style={{
            borderTop: `1px solid ${BORDER}`, paddingTop: "12px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            fontSize: "11px", color: "#888", fontFamily: FONT,
          }}>
            <span>Construction Manager Playbook{projectName ? ` — ${projectName}` : ""}</span>
            <span>Generated: {formatDate(new Date().toISOString())}</span>
          </div>

        </div>{/* /body */}
      </div>{/* /previewRef */}
      </div>{/* /outer wrapper */}
    </Dialog>
  );
};

export default WeeklyUpdatePreview;

